import {
  Injectable,
  computed,
  signal,
  Signal,
  WritableSignal,
  inject,
} from '@angular/core';
import { CurrentUserState } from '../../../../core/store/current-user/current-user.state';
import { Store } from '@ngxs/store';
import { ProductDetailsDTO, TopicArea } from '../models/ProductDTO';
import { firstValueFrom } from 'rxjs';
import { ProductService } from './product.service';
import { PaymentDataService } from '../../payment/services/payment-data.service';
import { FormControl } from '@angular/forms';
import { toSignal } from '@angular/core/rxjs-interop';
import { ProductSelectionDialogComponent } from '../components/product-selection-dialog/product-selection-dialog.component';
import { MatDialog } from '@angular/material/dialog';
import { DIALOG_SIZE_LARGE } from '../../../../shared/components/dialog/dialog-sizes';
import { SnackbarService } from '../../../../core/services/snackbar.service';
import { CustomPortalError } from '../../../../core/errors/models/CustomPortalError';
import { ErrorType } from '../../../../core/errors/models/ErrorType';

export interface ProductParams {
  quantity?: number;
  priceId?: string;
  purchaseIntentId?: string;
  promotionCode?: string;
  freeTrial: boolean;
}

@Injectable({ providedIn: 'root' })
export class ProductDataService {
  private readonly paymentDataService = inject(PaymentDataService);
  private readonly productService = inject(ProductService);
  private readonly store = inject(Store);
  private readonly dialog = inject(MatDialog);
  private readonly snackbarService = inject(SnackbarService);

  readonly quantity: Signal<number | null>;
  readonly promotionCode: Signal<string | null>;
  isPromotionCodeApplied = false;
  readonly freeTrial = signal<boolean>(false);
  readonly loading = signal<boolean>(false);
  readonly disableTopicAreaChanging = signal<boolean>(false);
  readonly productDetails = signal<ProductDetailsDTO | null>(null);
  readonly topicAreas = signal<TopicArea[]>([]);
  readonly trialingTopicAreaIds = signal<string[]>([]);

  readonly quantityControl: FormControl<number | null> = new FormControl<
    number | null
  >({
    value: null,
    disabled: this.loading(),
  });
  readonly promotionCodeControl: FormControl<string | null> = new FormControl<
    string | null
  >({
    value: null,
    disabled: this.loading(),
  });

  readonly priceId: WritableSignal<string | undefined> = signal(undefined);
  readonly initialQuantity: WritableSignal<number | undefined> =
    signal(undefined);
  readonly purchaseIntentId: WritableSignal<string | undefined> =
    signal(undefined);

  readonly price = computed(() => this.productDetails()?.price);

  readonly unitPrice = computed(() =>
    this.toEuros(this.price()?.unitAmountExcludingTaxAndDiscount),
  );

  readonly total = computed(() => this.toEuros(this.price()?.total));

  readonly discountAmount = computed(() =>
    this.toEuros(this.price()?.discountAmount),
  );

  readonly discountPercentage = computed(() =>
    Math.ceil(
      (this.discountAmount() / this.totalPriceWithoutTaxAndDiscount()) * 100,
    ),
  );

  readonly taxAmount = computed(() => this.toEuros(this.price()?.taxAmount));

  readonly taxPercentage = computed(() =>
    this.toEuros(this.price()?.taxPercentage),
  );

  readonly subtotal = computed(() => this.total() - this.taxAmount());

  readonly hasTax = computed(() => this.taxPercentage() > 0);

  readonly totalPriceWithoutTaxAndDiscount = computed(() => {
    const quantity = this.quantity();
    const unitPrice = this.unitPrice();
    if (!quantity) {
      return 0;
    }
    return unitPrice * quantity;
  });

  readonly promotionCodeApplied = computed(() => this.discountPercentage() > 0);

  readonly invalidPromotionCode = computed(
    () =>
      !!this.promotionCode() &&
      this.discountPercentage() === 0 &&
      this.discountAmount() === 0,
  );

  readonly isTrialingAllInOne = computed(
    () => this.trialingTopicAreaIds().length === this.topicAreas().length,
  );

  constructor() {
    this.store.select(CurrentUserState.priceId).subscribe(this.priceId.set);
    this.store.select(CurrentUserState.numberOfLicences).subscribe((value) => {
      this.initialQuantity.set(value);
      this.quantityControl.setValue(value);
    });
    this.store
      .select(CurrentUserState.purchaseIntentId)
      .subscribe(this.purchaseIntentId.set);
    this.quantity = toSignal(this.quantityControl.valueChanges, {
      initialValue: this.quantityControl.value,
    });
    this.promotionCode = toSignal(this.promotionCodeControl.valueChanges, {
      initialValue: this.promotionCodeControl.value,
    });
  }

  setProductParams(params: Partial<ProductParams>) {
    if (params.quantity !== undefined) {
      this.quantityControl.setValue(params.quantity);
      this.initialQuantity.set(params.quantity);
    }
    if (params.freeTrial !== undefined) {
      this.freeTrial.set(params.freeTrial);
    }
    if (params.promotionCode !== undefined) {
      this.promotionCodeControl.setValue(params.promotionCode);
    }
    if (params.priceId !== undefined) {
      this.priceId.set(params.priceId);
    }
    if (params.purchaseIntentId !== undefined) {
      this.purchaseIntentId.set(params.purchaseIntentId);
    }
  }

  resetProductPriceDetails() {
    const current = this.productDetails();
    if (!current) {
      return;
    }
    this.productDetails.update(() => ({
      ...current,
      price: {
        ...current.price,
        total: null,
        discountAmount: null,
        discountPercentage: null,
        taxAmount: null,
        taxPercentage: null,
      },
    }));
  }

  async getProductDetails() {
    const selectedCountry = this.paymentDataService.selectedCountry();
    const priceId = this.priceId();
    const quantity = this.quantity();
    const promotionCode = this.promotionCode();
    if (!priceId || !quantity) {
      return;
    }
    this.loading.set(true);
    this.isPromotionCodeApplied = false;
    try {
      const { product } = await firstValueFrom(
        this.productService.getProductDetails({
          quantity,
          promotionCode,
          selectedCountry,
          priceId,
        }),
      );
      if (!this.productDetails()) {
        this.onFirstLoad(product);
      }

      // prevent race condition
      if (quantity === this.quantity()) {
        this.productDetails.set(product);
      }
      if (this.promotionCode()) {
        this.isPromotionCodeApplied = true;
      }
    } catch (error) {
      const portalError = error as CustomPortalError;
      if (portalError.errorType === ErrorType.PROMO_CODE_INVALID) {
        this.snackbarService.error('PROMOTIONCODE_NOTACTIVE');
        if (this.promotionCode()) {
          this.isPromotionCodeApplied = false;
        }
      }
    }
    this.loading.set(false);
  }

  onFirstLoad(productDto: ProductDetailsDTO) {
    this.setTrialingTopicAreaIdsFromProductDetails(productDto);
    this.preOpenProductSelectionDialog();
  }

  preOpenProductSelectionDialog() {
    if (!this.disableTopicAreaChanging() && !this.isTrialingAllInOne()) {
      this.openProductSelectionDialog();
    }
  }

  setTrialingTopicAreaIdsFromProductDetails(productDto: ProductDetailsDTO) {
    // only set it once - when the product details are loaded for the first time
    if (this.trialingTopicAreaIds().length !== 0) {
      return;
    }
    const topicAreaIds = productDto.topicAreas.map(({ id }) => id);
    this.trialingTopicAreaIds.set(topicAreaIds);
  }

  toEuros(cents: number | null | undefined): number {
    return (cents ?? 0) / 100;
  }

  openProductSelectionDialog(enterAnimationDuration = 0) {
    document.body.style.overflow = 'hidden';
    const dialogRef = this.dialog.open(ProductSelectionDialogComponent, {
      ...DIALOG_SIZE_LARGE,
      enterAnimationDuration,
      exitAnimationDuration: 100,
    });
    dialogRef.afterClosed().subscribe(() => {
      document.body.style.overflow = '';
    });
  }
}
